import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { API_ENDPOINTS } from '../../config/api';
import AvatarFallback from '../../components/AvatarFallback/AvatarFallback';
import './Messages.css';



interface Message {
    id: string;
    text: string;
    timestamp: string;
    sender_id: string;
    conversation_id: string;
    is_read: boolean;
}

interface ConversationWithDetails {
    id: string;
    listing_id: string;
    user1_id: string;
    user2_id: string;
    participant_id: string;
    participant_name: string;
    participant_avatar?: string;
    latest_message_preview: string;
    unread_count: number;
    created_at: string;
    updated_at: string;
    last_message_timestamp?: string;
}

// Module-level cache — persists across back-navigation remounts
const messagesCacheStore: Record<string, { messages: Message[], nextToken: string | null, hasMore: boolean }> = {};

const Messages: React.FC = () => {
    const { isAuthenticated, user, isLoading } = useAuth();
    const { refreshUnreadCount } = useNotifications();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [selectedConversation, setSelectedConversation] = useState<ConversationWithDetails | null>(null);
    const [conversations, setConversations] = useState<ConversationWithDetails[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [showMobileChat, setShowMobileChat] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string>('');
    const [messagesLoading, setMessagesLoading] = useState(false);
    // messagesCache is module-level so it survives component unmount (back button)
    const [, forceRender] = useState(0);
    const setMessagesCache = (updater: (prev: typeof messagesCacheStore) => typeof messagesCacheStore) => {
        const next = updater(messagesCacheStore);
        Object.assign(messagesCacheStore, next);
        forceRender(n => n + 1);
    };
    const messagesCache = messagesCacheStore;

    // Pagination states
    const [hasMoreConversations, setHasMoreConversations] = useState(false);
    const [nextConversationToken, setNextConversationToken] = useState<string | null>(null);
    const conversationsObserverTarget = useRef<HTMLDivElement>(null);

    // Message pagination states
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [nextMessageToken, setNextMessageToken] = useState<string | null>(null);
    const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
    const messagesTopObserverTarget = useRef<HTMLDivElement>(null);

    // Real-time polling refs and state
    const conversationsPollingRef = useRef<NodeJS.Timeout | null>(null);
    const messagesPollingRef = useRef<NodeJS.Timeout | null>(null);
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [isUserActive, setIsUserActive] = useState(true);
    const lastMessageCountRef = useRef<number>(0);
    const currentConversationRef = useRef<string | null>(null);
    const sendingRef = useRef(false);
    const [isSending, setIsSending] = useState(false);


    // User activity detection
    useEffect(() => {
        const handleActivity = () => setIsUserActive(true);
        const handleInactivity = () => setIsUserActive(false);

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keypress', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('scroll', handleActivity);

        let inactivityTimer: NodeJS.Timeout;
        const resetInactivityTimer = () => {
            clearTimeout(inactivityTimer);
            setIsUserActive(true);
            inactivityTimer = setTimeout(handleInactivity, 5 * 60 * 1000);
        };

        resetInactivityTimer();
        window.addEventListener('mousemove', resetInactivityTimer);
        window.addEventListener('keypress', resetInactivityTimer);
        window.addEventListener('click', resetInactivityTimer);

        return () => {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keypress', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('scroll', handleActivity);
            window.removeEventListener('mousemove', resetInactivityTimer);
            window.removeEventListener('keypress', resetInactivityTimer);
            window.removeEventListener('click', resetInactivityTimer);
            clearTimeout(inactivityTimer);
        };
    }, []);

    useEffect(() => {
        if (isAuthenticated && user?.id) {
            fetchConversations(true);
            startConversationsPolling();
        } else {
            stopAllPolling();
        }

        return () => {
            stopAllPolling();
        };
    }, [isAuthenticated, user?.id]);

    // Prevent body scrolling on mobile - only for messages page
    useEffect(() => {
        if (window.innerWidth <= 768) {
            document.body.classList.add('messages-page-active');

            return () => {
                document.body.classList.remove('messages-page-active');
            };
        }
    }, []);

    // Main data fetching functions
    const fetchMessagesWithScrollPreservation = useCallback(async (conversationId: string) => {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) return;

            if (currentConversationRef.current !== conversationId) {
                return;
            }

            const response = await axios.get(
                API_ENDPOINTS.MESSAGES.CONVERSATION_MESSAGES(conversationId),
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const newMessages = response.data.messages;
            const pagination = response.data.pagination || {};

            const wasAtBottom = messagesContainerRef.current ?
                messagesContainerRef.current.scrollTop + messagesContainerRef.current.clientHeight >=
                messagesContainerRef.current.scrollHeight - 100 : true;

            setMessages(prevMessages => {
                if (currentConversationRef.current !== conversationId) {
                    return prevMessages || [];
                }

                const safePrevMessages = prevMessages || [];
                const safeNewMessages = newMessages || [];

                if (JSON.stringify(safePrevMessages) !== JSON.stringify(safeNewMessages)) {
                    lastMessageCountRef.current = safeNewMessages.length;

                    const hasUnreadMessages = safeNewMessages.some((msg: any) =>
                        msg.sender_id !== user?.id && !msg.is_read
                    );

                    if (hasUnreadMessages) {
                        axios.post(
                            API_ENDPOINTS.MESSAGES.MARK_READ(conversationId),
                            {},
                            {
                                headers: {
                                    'Authorization': `Bearer ${token}`
                                }
                            }
                        ).then(() => {
                            setConversations(prevConversations =>
                                prevConversations.map(conv =>
                                    conv.id === conversationId
                                        ? { ...conv, unread_count: 0 }
                                        : conv
                                )
                            );
                            refreshUnreadCount();
                        }).catch((markReadError) => {
                            console.error('Error marking conversation as read during polling:', markReadError);
                        });
                    }

                    setTimeout(() => {
                        if (wasAtBottom && messagesContainerRef.current && currentConversationRef.current === conversationId) {
                            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                        }
                    }, 50);

                    return safeNewMessages;
                }
                return safePrevMessages;
            });

            // Update message pagination state
            const nextToken = pagination.next_token || null;
            const hasMore = pagination.has_more || false;
            setNextMessageToken(nextToken);
            setHasMoreMessages(hasMore);

            // Save to memory cache
            setMessagesCache(prevCache => ({
                ...prevCache,
                [conversationId]: {
                    messages: newMessages || [],
                    nextToken: nextToken,
                    hasMore: hasMore
                }
            }));
        } catch (error: any) {
            console.error('Error fetching messages for polling:', error);
        }
    }, [user?.id, refreshUnreadCount]);

    const fetchConversations = useCallback(async (showLoading: boolean = true, isLoadMore: boolean = false) => {
        try {
            if (showLoading && !isLoadMore) {
                setLoading(true);
            }
            if (isLoadMore) {
                setLoadingMore(true);
            }

            const token = localStorage.getItem('auth_token');
            if (!token) {
                setError('Authentication token not found');
                return;
            }

            if (!user?.id) {
                console.log('User not available yet, skipping conversation fetch');
                return;
            }

            // Build URL with pagination - use enriched endpoint
            const url = new URL(API_ENDPOINTS.MESSAGES.ENRICHED_CONVERSATIONS);
            url.searchParams.append('limit', '20');
            if (isLoadMore && nextConversationToken) {
                url.searchParams.append('last_evaluated_key', nextConversationToken);
            }

            const response = await axios.get(url.toString(), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const newConversations = response.data.data || [];
            const pagination = response.data.pagination || {};

            // Conversations are already sorted by the backend
            if (isLoadMore) {
                setConversations(prev => [...prev, ...newConversations]);
            } else {
                setConversations(newConversations);
            }

            setNextConversationToken(pagination.next_token || null);
            setHasMoreConversations(pagination.has_more || false);

            if (currentConversationRef.current) {
                const updatedSelectedConversation = newConversations.find(
                    (conv: ConversationWithDetails) => conv.id === currentConversationRef.current
                );

                if (updatedSelectedConversation) {
                    setSelectedConversation(updatedSelectedConversation);
                    if (currentConversationRef.current === updatedSelectedConversation.id) {
                        fetchMessagesWithScrollPreservation(updatedSelectedConversation.id);
                    }
                }
            }

            setError('');
        } catch (error: any) {
            console.error('Error fetching conversations:', error);
            setError('Failed to load conversations');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [user?.id, nextConversationToken, fetchMessagesWithScrollPreservation]);

    // Load more conversations
    const loadMoreConversations = useCallback(() => {
        if (!loadingMore && hasMoreConversations && nextConversationToken) {
            fetchConversations(false, true);
        }
    }, [loadingMore, hasMoreConversations, nextConversationToken, fetchConversations]);

    // Intersection Observer for infinite scroll on conversations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loading && !loadingMore && hasMoreConversations) {
                    loadMoreConversations();
                }
            },
            { threshold: 0.1 }
        );

        if (conversationsObserverTarget.current) {
            observer.observe(conversationsObserverTarget.current);
        }

        return () => {
            if (conversationsObserverTarget.current) {
                observer.unobserve(conversationsObserverTarget.current);
            }
        };
    }, [loading, loadingMore, hasMoreConversations, loadMoreConversations]);

    // Load more messages function (defined here before useEffect uses it)
    const loadMoreMessagesCallback = useCallback(async () => {
        if (!selectedConversation || !nextMessageToken || loadingMoreMessages || !hasMoreMessages) {
            return;
        }

        try {
            setLoadingMoreMessages(true);
            const token = localStorage.getItem('auth_token');
            if (!token) return;

            // Save current scroll position
            const scrollContainer = messagesContainerRef.current;
            const oldScrollHeight = scrollContainer?.scrollHeight || 0;

            // Build URL with pagination
            const url = new URL(API_ENDPOINTS.MESSAGES.CONVERSATION_MESSAGES(selectedConversation.id));
            url.searchParams.append('limit', '50');
            url.searchParams.append('last_evaluated_key', nextMessageToken);

            const response = await axios.get(url.toString(), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const olderMessages = response.data.messages || [];
            const pagination = response.data.pagination || {};

            // Prepend older messages to the beginning of the list
            setMessages(prevMessages => [...olderMessages, ...(prevMessages || [])]);

            // Update pagination state
            setNextMessageToken(pagination.next_token || null);
            setHasMoreMessages(pagination.has_more || false);

            // Restore scroll position to prevent jumping
            setTimeout(() => {
                if (scrollContainer) {
                    const newScrollHeight = scrollContainer.scrollHeight;
                    const scrollDiff = newScrollHeight - oldScrollHeight;
                    scrollContainer.scrollTop = scrollDiff;
                }
            }, 50);
        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            setLoadingMoreMessages(false);
        }
    }, [selectedConversation, nextMessageToken, loadingMoreMessages, hasMoreMessages]);

    // Intersection Observer for infinite scroll on messages (load older messages)
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMoreMessages && hasMoreMessages) {
                    loadMoreMessagesCallback();
                }
            },
            { threshold: 0.1 }
        );

        if (messagesTopObserverTarget.current) {
            observer.observe(messagesTopObserverTarget.current);
        }

        return () => {
            if (messagesTopObserverTarget.current) {
                observer.unobserve(messagesTopObserverTarget.current);
            }
        };
    }, [loadingMoreMessages, hasMoreMessages, loadMoreMessagesCallback]);

    // Polling control functions
    const stopAllPolling = useCallback(() => {
        if (conversationsPollingRef.current) {
            clearInterval(conversationsPollingRef.current);
            conversationsPollingRef.current = null;
        }
        if (messagesPollingRef.current) {
            clearInterval(messagesPollingRef.current);
            messagesPollingRef.current = null;
        }
    }, []);

    const startConversationsPolling = useCallback(() => {
        if (conversationsPollingRef.current) {
            clearInterval(conversationsPollingRef.current);
        }

        const pollInterval = isUserActive ? 30000 : 60000;
        conversationsPollingRef.current = setInterval(() => {
            if (isAuthenticated) {
                fetchConversations(false, false);
            }
        }, pollInterval);
    }, [isAuthenticated, isUserActive, fetchConversations]);

    const startMessagesPolling = useCallback((conversationId: string) => {
        if (messagesPollingRef.current) {
            clearInterval(messagesPollingRef.current);
            messagesPollingRef.current = null;
        }

        currentConversationRef.current = conversationId;

        const pollInterval = isUserActive ? 15000 : 30000;
        messagesPollingRef.current = setInterval(() => {
            if (isAuthenticated && currentConversationRef.current === conversationId) {
                fetchMessagesWithScrollPreservation(conversationId);
            }
        }, pollInterval);
    }, [isAuthenticated, isUserActive, fetchMessagesWithScrollPreservation]);

    // Restart polling when user activity changes
    useEffect(() => {
        if (isAuthenticated) {
            startConversationsPolling();

            if (currentConversationRef.current) {
                startMessagesPolling(currentConversationRef.current);
            }
        }
    }, [isUserActive, isAuthenticated, startConversationsPolling, startMessagesPolling]);

    // Auto-select conversation based on userId parameter
    // Auto-select conversation based on userId parameter
    useEffect(() => {
        const userId = searchParams.get('userId');
        const listingId = searchParams.get('listingId') || searchParams.get('listing_id');

        // We wait for loading to finish to ensure we have the initial list of conversations
        // UNLESS we have no conversations (empty list) and loading is done, then we MUST check if we need to create one.
        if (userId && !selectedConversation && !loading) {
            const initiateConversation = async () => {
                // 1. Try to find in existing conversations
                const userConversations = conversations.filter(conv => conv.participant_id === userId);

                let targetConversation: ConversationWithDetails | undefined;

                if (userConversations.length > 0) {
                    const getConversationSortKey = (conv: ConversationWithDetails): number => {
                        const raw = conv.last_message_timestamp || conv.updated_at || conv.created_at;
                        const t = raw ? Date.parse(raw) : 0;
                        return Number.isFinite(t) ? t : 0;
                    };

                    const mostRecentUserConversation = userConversations.reduce((best, current) =>
                        getConversationSortKey(current) > getConversationSortKey(best) ? current : best
                    );

                    targetConversation = (listingId ? userConversations.find(conv => conv.listing_id === listingId) : undefined) || mostRecentUserConversation;
                }

                if (targetConversation) {
                    handleConversationClick(targetConversation);
                    // Clear params
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('userId');
                    newSearchParams.delete('listingId');
                    newSearchParams.delete('listing_id');
                    setSearchParams(newSearchParams);
                    return;
                }

                // 2. If not found locally, try to fetch or create
                try {
                    const token = localStorage.getItem('auth_token');
                    if (!token) return;

                    let conversationData: any = null;

                    // Try creating/getting via POST if listingId exists
                    if (listingId) {
                        try {
                            const res = await axios.post(
                                API_ENDPOINTS.MESSAGES.CREATE_CONVERSATION,
                                { participant_id: userId, listing_id: listingId },
                                { headers: { 'Authorization': `Bearer ${token}` } }
                            );
                            conversationData = res.data;
                        } catch (e) {
                            console.log('Could not create conversation via POST, trying GET', e);
                        }
                    }

                    // Fallback to GET by user ID (checking if it exists)
                    if (!conversationData) {
                        try {
                            const res = await axios.get(
                                API_ENDPOINTS.MESSAGES.WITH_USER(userId),
                                { headers: { 'Authorization': `Bearer ${token}` } }
                            );
                            conversationData = res.data;
                        } catch (e) {
                            console.log('Conversation does not exist yet', e);
                        }
                    }

                    if (conversationData) {
                        // Fetch user details for the UI
                        const userRes = await axios.get(
                            API_ENDPOINTS.USERS.GET_USER(userId),
                            { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        const userData = userRes.data;

                        const enrichedConv: ConversationWithDetails = {
                            id: conversationData.id,
                            listing_id: conversationData.listing_id,
                            user1_id: conversationData.user1_id,
                            user2_id: conversationData.user2_id,
                            participant_id: userId,
                            participant_name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.email || 'User',
                            participant_avatar: userData.avatar_url,
                            latest_message_preview: '',
                            unread_count: 0,
                            created_at: conversationData.created_at,
                            updated_at: conversationData.updated_at,
                            last_message_timestamp: conversationData.last_message_timestamp
                        };

                        // Add to list and select
                        setConversations(prev => {
                            // Avoid duplicates
                            if (prev.some(c => c.id === enrichedConv.id)) return prev;
                            return [enrichedConv, ...prev];
                        });
                        handleConversationClick(enrichedConv);

                        // Clear params
                        const newSearchParams = new URLSearchParams(searchParams);
                        newSearchParams.delete('userId');
                        newSearchParams.delete('listingId');
                        newSearchParams.delete('listing_id');
                        setSearchParams(newSearchParams);
                    }
                } catch (error) {
                    console.error("Error initializing conversation:", error);
                }
            };

            initiateConversation();
        }
    }, [conversations, searchParams, selectedConversation, loading, setSearchParams]);

    const fetchMessages = async (conversationId: string) => {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) {
                setError('Authentication token not found');
                return;
            }

            const response = await axios.get(
                API_ENDPOINTS.MESSAGES.CONVERSATION_MESSAGES(conversationId),
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const messagesList = response.data.messages || [];
            setMessages(messagesList);

            // Update message pagination state
            const pagination = response.data.pagination || {};
            const nextToken = pagination.next_token || null;
            const hasMore = pagination.has_more || false;
            setNextMessageToken(nextToken);
            setHasMoreMessages(hasMore);

            // Save to memory cache
            setMessagesCache(prevCache => ({
                ...prevCache,
                [conversationId]: {
                    messages: messagesList,
                    nextToken: nextToken,
                    hasMore: hasMore
                }
            }));

            try {
                await axios.post(
                    API_ENDPOINTS.MESSAGES.MARK_READ(conversationId),
                    {},
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                setConversations(prevConversations =>
                    prevConversations.map(conv =>
                        conv.id === conversationId
                            ? { ...conv, unread_count: 0 }
                            : conv
                    )
                );
                refreshUnreadCount();
            } catch (markReadError) {
                console.error('Error marking conversation as read:', markReadError);
            }
        } catch (error: any) {
            console.error('Error fetching messages:', error);
            setError('Failed to load messages');
        }
    };

    const handleConversationClick = async (conversation: ConversationWithDetails) => {
        if (selectedConversation?.id === conversation.id) {
            setShowMobileChat(true);
            return;
        }

        if (messagesPollingRef.current) {
            clearInterval(messagesPollingRef.current);
            messagesPollingRef.current = null;
        }

        currentConversationRef.current = conversation.id;

        setSelectedConversation(conversation);
        setShowMobileChat(true);

        const cached = messagesCache[conversation.id];
        if (cached) {
            setMessages(cached.messages);
            setNextMessageToken(cached.nextToken);
            setHasMoreMessages(cached.hasMore);
            setMessagesLoading(false);

            // Quietly fetch in background to refresh messages
            fetchMessages(conversation.id).catch(console.error);
        } else {
            // Reset message pagination state
            setNextMessageToken(null);
            setHasMoreMessages(false);
            setMessages([]);
            setMessagesLoading(true);

            try {
                await fetchMessages(conversation.id);
            } finally {
                setMessagesLoading(false);
            }
        }

        setTimeout(() => {
            if (messagesContainerRef.current) {
                messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
        }, 100);

        startMessagesPolling(conversation.id);
    };

    const handleBackToConversations = () => {
        // A draft conversation is only kept on screen while its chat is open.
        // If no message was sent, it should not become a conversation-list entry.
        if (selectedConversation && messages.length === 0) {
            setSelectedConversation(null);
        }
        setShowMobileChat(false);
        setMessages([]);
        setNextMessageToken(null);
        setHasMoreMessages(false);

        currentConversationRef.current = null;

        if (messagesPollingRef.current) {
            clearInterval(messagesPollingRef.current);
            messagesPollingRef.current = null;
        }
    };

    const handleSendMessage = useCallback(async () => {
        if (!newMessage.trim() || !selectedConversation) return;
        if (sendingRef.current) return;
        sendingRef.current = true;
        setIsSending(true);

        const conversationIdToUse = selectedConversation.id;
        const messageText = newMessage.trim();

        try {
            const token = localStorage.getItem('auth_token');
                if (!token) {
                    setError('Authentication token not found');
                    return;
                }

                // Temporarily stop polling to prevent race condition
                if (messagesPollingRef.current) {
                    clearInterval(messagesPollingRef.current);
                    messagesPollingRef.current = null;
                }

                const messageData = {
                    text: messageText,
                    conversation_id: conversationIdToUse
                };

                const response = await axios.post(
                    API_ENDPOINTS.MESSAGES.SEND_MESSAGE(conversationIdToUse),
                    messageData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                // Add message to local state immediately for responsive UI
                setMessages(prevMessages => [...(prevMessages || []), response.data]);
                setConversations(prevConversations => prevConversations.map(conv =>
                    conv.id === conversationIdToUse
                        ? {
                            ...conv,
                            latest_message_preview: response.data.text,
                            updated_at: response.data.timestamp,
                            last_message_timestamp: response.data.timestamp
                        }
                        : conv
                ));
                setNewMessage('');

                // Reset textarea height
                if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                }

                setTimeout(() => {
                    if (messagesContainerRef.current) {
                        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
                    }
                }, 50);

                // Wait for DynamoDB to propagate, then fetch fresh data and resume polling
                setTimeout(async () => {
                    try {
                        // Fetch fresh messages from server to ensure consistency
                        await fetchMessagesWithScrollPreservation(conversationIdToUse);
                        // Update conversations to refresh preview and timestamps
                        await fetchConversations(false, false);
                        refreshUnreadCount();
                        // Resume polling
                        startMessagesPolling(conversationIdToUse);
                    } catch (error) {
                        console.error('Error refreshing after message send:', error);
                        // Resume polling even if refresh failed
                        startMessagesPolling(conversationIdToUse);
                    }
                }, 1000); // Increased to 1 second to ensure DynamoDB consistency
            } catch (error: any) {
                console.error('Error sending message:', error);
                setError('Failed to send message');
                // Resume polling even on error
                startMessagesPolling(conversationIdToUse);
            } finally {
                sendingRef.current = false;
                setIsSending(false);
            }
    }, [newMessage, selectedConversation, fetchMessagesWithScrollPreservation, fetchConversations, refreshUnreadCount, startMessagesPolling]);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!sendingRef.current) handleSendMessage();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNewMessage(e.target.value);

        // Auto-resize textarea
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
        }
    };

    // Scroll to bottom when conversation changes or messages load
    useEffect(() => {
        if (selectedConversation?.id && messages && messages.length > 0) {
            const scrollContainer = messagesContainerRef.current;
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
                
                const timer = setTimeout(() => {
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                }, 50);
                
                return () => clearTimeout(timer);
            }
        }
    }, [selectedConversation?.id, messages]);

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate('/?login=true');
        }
    }, [isAuthenticated, isLoading, navigate]);

    if (!isAuthenticated) {
        return null; // Will redirect via useEffect
    }

    return (
        <div className="messages-page">
            <NavBar inverted={true} />
            <main className="messages-main">
                <div className="messages-container">

                    {error && (
                        <div className="error-message" style={{
                            backgroundColor: '#fee',
                            color: '#c33',
                            padding: '10px',
                            borderRadius: '4px',
                            margin: '10px 0'
                        }}>
                            {error}
                        </div>
                    )}


                    <div className="messages-content">
                        <div className={`conversations-sidebar ${showMobileChat ? 'mobile-hidden' : ''}`}>
                            <div className="conversations-header">
                                <h2>Conversations</h2>
                            </div>
                            <div className="conversations-list">

                                {loading ? (
                                    <div className="conversations-skeleton-list">
                                        {[...Array(4)].map((_, idx) => (
                                            <div key={idx} className="conversation-skeleton-item">
                                                <div className="conversation-skeleton-avatar"></div>
                                                <div className="conversation-skeleton-info">
                                                    <div className="conversation-skeleton-name"></div>
                                                    <div className="conversation-skeleton-preview"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    ) : (() => {
                                        // Empty conversations are temporary compose states, not inbox entries.
                                        const filteredConversations = conversations.filter(
                                            conv => conv.participant_name !== 'Unknown User' &&
                                                Boolean(conv.latest_message_preview) &&
                                                conv.latest_message_preview !== 'No messages yet'
                                        );

                                        return filteredConversations.length === 0 ? (
                                        <div className="conversations-list-message">
                                            No conversations yet.
                                        </div>
                                    ) : (
                                        <>
                                            {filteredConversations.map((conversation, index) => (
                                                <div
                                                    key={conversation.id || `conversation-${index}`}

                                                    className={`conversation-item ${selectedConversation?.id === conversation.id ? 'active' : ''}`}
                                                    onClick={() => handleConversationClick(conversation)}
                                                >
                                                    <div className="conversation-avatar">
                                                        {conversation.participant_avatar ? (
                                                            <img
                                                                src={conversation.participant_avatar}
                                                                alt={conversation.participant_name}
                                                                className="conversation-avatar-img"
                                                                loading="lazy"
                                                                onError={(e) => {
                                                                    const target = e.target as HTMLImageElement;
                                                                    const placeholder = target.nextElementSibling as HTMLElement;
                                                                    if (placeholder) {
                                                                        target.style.display = 'none';
                                                                        placeholder.style.display = 'flex';
                                                                    }
                                                                }}
                                                            />
                                                        ) : null}
                                                        <div
                                                            className="message-avatar-placeholder"
                                                            style={{
                                                                display: conversation.participant_avatar ? 'none' : 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <AvatarFallback
                                                                name={conversation.participant_name}
                                                                size={48}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="conversation-info">
                                                        <div className="conversation-name">{conversation.participant_name}</div>
                                                        <div className="conversation-preview">{conversation.latest_message_preview || 'No messages yet'}</div>
                                                        <div className="conversation-time">
                                                            {new Date(conversation.last_message_timestamp || conversation.updated_at).toLocaleDateString()}
                                                        </div>
                                                        {conversation.unread_count > 0 && (
                                                            <div className="unread-badge">{conversation.unread_count}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Loading indicator and observer target */}
                                            <div ref={conversationsObserverTarget} className="load-more-trigger">
                                                {loadingMore && (
                                                    <div className="conversation-skeleton-item" style={{ borderBottom: 'none', padding: '0.5rem 1.5rem' }}>
                                                        <div className="conversation-skeleton-avatar"></div>
                                                        <div className="conversation-skeleton-info">
                                                            <div className="conversation-skeleton-name"></div>
                                                            <div className="conversation-skeleton-preview"></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        <div className={`usub-messages-panel ${showMobileChat ? 'mobile-visible' : ''}`}>
                            {selectedConversation ? (
                                <div className="chat-container">
                                    <div className="chat-header">
                                        <button className="chat-back-button" onClick={handleBackToConversations}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M19 12H5M12 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                        <div className="chat-avatar-container">
                                            {selectedConversation.participant_avatar ? (
                                                <img
                                                    src={selectedConversation.participant_avatar}
                                                    alt={selectedConversation.participant_name}
                                                    className="chat-avatar-img"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        const placeholder = target.nextElementSibling as HTMLElement;
                                                        if (placeholder) {
                                                            target.style.display = 'none';
                                                            placeholder.style.display = 'flex';
                                                        }
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className="message-avatar-placeholder small"
                                                style={{
                                                    display: selectedConversation.participant_avatar ? 'none' : 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <AvatarFallback
                                                    name={selectedConversation.participant_name}
                                                    size={40}
                                                />
                                            </div>
                                        </div>
                                        <div className="chat-user-name">{selectedConversation.participant_name}</div>
                                        <button
                                            type="button"
                                            className="go-to-listing-btn"
                                            onClick={() => navigate(`/listing/${selectedConversation.listing_id}`)}
                                        >
                                            Go to Listing
                                        </button>
                                    </div>

                                    <div className="chat-messages" ref={messagesContainerRef}>
                                        {messagesLoading ? (
                                            <div className="chat-messages-loading">
                                                <div className="profile-loading-dots" aria-label="Loading messages" role="status">
                                                    <span />
                                                    <span />
                                                    <span />
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Loading indicator for older messages at the top */}
                                                {hasMoreMessages && (
                                                    <div ref={messagesTopObserverTarget} className="load-more-messages-trigger" style={{ padding: 0 }}>
                                                        {loadingMoreMessages && (
                                                            <div className="message-skeleton-container">
                                                                <div className="message-skeleton bubble-left"></div>
                                                                <div className="message-skeleton bubble-right"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {messages && messages.length > 0 ? (() => {
                                                    // Calculate date separators once outside the map
                                                    const today = new Date();
                                                    const yesterday = new Date(today);
                                                    yesterday.setDate(yesterday.getDate() - 1);

                                                    const getDateLabel = (messageDate: Date) => {
                                                        const isToday =
                                                            messageDate.getFullYear() === today.getFullYear() &&
                                                            messageDate.getMonth() === today.getMonth() &&
                                                            messageDate.getDate() === today.getDate();

                                                        const isYesterday =
                                                            messageDate.getFullYear() === yesterday.getFullYear() &&
                                                            messageDate.getMonth() === yesterday.getMonth() &&
                                                            messageDate.getDate() === yesterday.getDate();

                                                        if (isToday) {
                                                            return 'Today';
                                                        } else if (isYesterday) {
                                                            return 'Yesterday';
                                                        } else {
                                                            return messageDate.toLocaleDateString('en-US', {
                                                                month: 'long',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            });
                                                        }
                                                    };

                                                    const lastIdx = messages.length - 1;

                                                    return messages.map((message, index) => {
                                                        const isOwn = message.sender_id === user?.id;
                                                        const messageDate = new Date(message.timestamp);
                                                        const isLastInThread = index === lastIdx;
                                                        const showReadReceipt =
                                                            isOwn &&
                                                            isLastInThread &&
                                                            Boolean(message.is_read);

                                                        const dateLabel = getDateLabel(messageDate);
                                                        const previousMessage = index > 0 ? messages[index - 1] : null;
                                                        const previousMessageDate = previousMessage ? new Date(previousMessage.timestamp) : null;
                                                        const showDateSeparator =
                                                            !previousMessageDate ||
                                                            getDateLabel(previousMessageDate) !== dateLabel;

                                                        return (
                                                            <React.Fragment key={message.id || `message-${index}`}>
                                                                {showDateSeparator && (
                                                                    <div className="date-separator">
                                                                        <span className="date-separator-text">{dateLabel}</span>
                                                                    </div>
                                                                )}
                                                                <div className={`message ${isOwn ? 'own' : 'other'}`}>
                                                                    <div className="message-body">
                                                                        <div className="message-bubble">
                                                                            <div className="message-text">{message.text}</div>
                                                                            <div className="message-time">
                                                                                {new Date(message.timestamp).toLocaleTimeString([], {
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit'
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                        {showReadReceipt && (
                                                                            <div className="message-read-receipt">Read</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </React.Fragment>
                                                        );
                                                    });
                                                })() : (
                                                    <div className="no-messages">
                                                        <p>No messages in this conversation yet.</p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className="chat-input-container">
                                        <div className="chat-input-wrapper">
                                            <textarea
                                                ref={textareaRef}
                                                className="chat-input"
                                                placeholder="Type a message..."
                                                value={newMessage}
                                                onChange={handleInputChange}
                                                onKeyPress={handleKeyPress}
                                                rows={1}
                                            />
                                            <button
                                                className="send-button"
                                                onClick={handleSendMessage}
                                                disabled={!newMessage.trim() || isSending}
                                            >
                                                Send
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-content">
                                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3, marginBottom: '1rem' }}>
                                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        </svg>
                                        <h3>Select a conversation</h3>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Messages;
